import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
  useDisclosure,
} from "@nextui-org/react";
import React, { useEffect, useMemo, useState } from "react";
import ApiModal from "./ApiModal";
import {
  DeleteOnedata,
  getDataWithFilterUrl,
} from "../../../controllers/APIController";
import { DateFormatorDD } from "../../../utils/helper";
import ApiEditModal from "./ApiEditModal";
import LoadingPage from "../../../components/Loader/LoadingPage";
import NullData from "../../../components/ErrorPage/NullData";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";


/**
 * ApiKey Component
 * Manages API keys for emissions metrics submissions
 * Provides functionality to create, edit, and delete API keys
 * @returns {JSX.Element} API key management interface
 */
const ApiKey = () => {
  // Modal disclosure hooks
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const {
    isOpen: isOpenEdit,
    onOpen: onOpenEdit,
    onOpenChange: onOpenChangeEdit,
  } = useDisclosure();

  // Component state
  const [selectedId, setSelectedId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [apiKeyData, setApiKeyData] = useState([]);
  const queryClient = useQueryClient();
  // User and organization data from session storage
  const userLogged = JSON.parse(sessionStorage?.getItem("userData") ?? null);
  const orgInfo = userLogged && userLogged?.user?.LastOrgId;;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + orgInfo;
  const userEmail = userLogged && userLogged?.user?.email;

  // Table configuration
  const tableHeaders = ["Name", "Key", "Created At", "Responses", "Actions"];
  const maskedApiKey = "************************";
  const itemsPerPage = 20;
  const totalPages = Math.ceil(apiKeyData.length / itemsPerPage);

  /**
   * Refresh API key data after changes
   * Toggles reRender state to trigger useEffect
   */
  const handleSubmit = () => {
    queryClient?.invalidateQueries({ queryKey: ["keys"] });
  };

  /**
   * Open create API key modal
   * Triggers the create modal disclosure
   */
  const handleCreate = () => {
    onOpen();
  };

  /**
   * Delete API key by ID
   * Removes the specified API key and refreshes data
   * @param {string} id - ID of the API key to delete
   */
  const handleDeleteKey = async (id) => {
    try {
      await DeleteOnedata("keys", id);
       queryClient?.invalidateQueries({ queryKey: ["keys"] });
    } catch (error) {
      console.error("Error deleting API key:", error);
    }
  };

  /**
   * Fetch API key data on component mount and re-render
   * Loads organization-specific API keys filtered by user email
   */

 const { data: apiKey } = useQuery({
   queryKey: ["keys"],
   queryFn: () => getDataWithFilterUrl("keys", orgFilterUrl),
 });

 const filterApiKeyData = useMemo(() => {
   return apiKey?.data?.filter(
     (item) =>
       item?.attributes?.users_permissions_user?.data?.attributes?.email ===
       userEmail
   );
 }, [orgFilterUrl, userEmail, apiKey?.data]);

 useEffect(() => {
   setApiKeyData(filterApiKeyData ?? []);
   setIsLoading(false);
 }, [filterApiKeyData]);



  return (
    <div>
      {/* Create API Key Modal */}
      {isOpen && (
        <ApiModal
          onSubmitHandler={handleSubmit}
          isOpen={isOpen}
          onOpen={onOpen}
          onOpenChange={onOpenChange}
          id={Date.now()}
        />
      )}
      
      {/* Edit API Key Modal */}
      {isOpenEdit && (
        <ApiEditModal
          onSubmitHandler={handleSubmit}
          isOpen={isOpenEdit}
          onOpen={onOpenEdit}
          onOpenChange={onOpenChangeEdit}
          id={selectedId}
        />
      )}
      
      {/* Page Description */}
      <div className="my-8">
        <p>
          Manage your API keys for emissions metrics submissions here. Create,
          edit, or delete your API keys as needed.
        </p>
      </div>
      
      {/* Create Button */}
      <div className="flex justify-end cursor-pointer">
        <Button 
          color="primary" 
          className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 font-medium"
          onClick={handleCreate}
        >
          Create API Key
        </Button>
      </div>
      
      {/* API Keys Table */}
      <LoadingPage isLoading={isLoading}>
        <div className="mt-8">
          <Table
            removeWrapper
            selectionMode="single"
            aria-label="api-key-table"
          >
            {/* Table Header */}
            <TableHeader>
              {tableHeaders.map((header, headerIndex) => (
                <TableColumn key={headerIndex}>{header}</TableColumn>
              ))}
            </TableHeader>
            
            {/* Table Body */}
            <TableBody
              emptyContent={
                <NullData
                  title="No API key found. Click on 'create' to make a new key"
                />
              }
            >
              {apiKeyData.map((item, index) => {
                const lowerPageLimit = (currentPage - 1) * itemsPerPage;
                const upperPageLimit = currentPage * itemsPerPage;
                
                if (index < lowerPageLimit || index >= upperPageLimit) {
                  return null;
                }

                return (
                  <TableRow
                    key={item.id}
                    onClick={() => {
                      setSelectedId(item.id);
                      onOpenEdit();
                    }}
                    className="cursor-pointer"
                  >
                    {/* API Key Name */}
                    <TableCell>{item?.attributes?.name}</TableCell>
                    
                    {/* Masked API Key */}
                    <TableCell>{maskedApiKey}</TableCell>
                    
                    {/* Creation Date */}
                    <TableCell>
                      {DateFormatorDD(item?.attributes?.createdAt)}
                    </TableCell>
                    
                    {/* Response Count */}
                    <TableCell>{item?.attributes?.Logs?.length || 0}</TableCell>

                    {/* Actions */}
                    <TableCell>
                      <div className="flex gap-4 items-center cursor-pointer">
                        <Dropdown>
                          <DropdownTrigger>
                            <div className="cursor-pointer">
                              <Tooltip size="sm" content="Delete API Key">
                                <Trash2 className="w-[20px] h-[20px]"
                                />
                              </Tooltip>
                            </div>
                          </DropdownTrigger>
                          <DropdownMenu
                            aria-label="Static Actions"
                            selectionMode="single"
                            onSelectionChange={() => {
                              handleDeleteKey(item?.id);
                            }}
                          >
                            <DropdownItem
                              key="delete"
                              color="danger"
                              variant="flat"
                            >
                              Delete {item?.attributes?.name}?
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        
        {/* Pagination Controls */}
        {apiKeyData.length > 0 && (
          <div className="mt-4">
            <div className="w-full mt-8 flex items-center justify-center">
              <Pagination
                showControls
                total={totalPages}
                color="secondary"
                page={currentPage}
                onChange={setCurrentPage}
              />
            </div>
          </div>
        )}
      </LoadingPage>
    </div>
  );
};

export default ApiKey;
